/**
 * Boundary Layer — prepares style configs for admin boundary rendering
 * on the MapLibre globe.
 *
 * No longer creates Deck.gl layers. Instead, exports prepareBoundaryStyle()
 * which returns enriched GeoJSON + MapLibre paint objects for native rendering.
 *
 * Display modes:
 *   'outline'               — yellow outlines only (for "show me the states of X")
 *   'choropleth-population'  — blue gradient fill based on population
 *   'choropleth-density'     — red gradient fill based on pop_density_km2
 *   'choropleth-share'       — purple gradient fill based on pop_pct_national
 *   'highlight'              — single region highlight (yellow outline + translucent fill)
 *   'political'              — world political map: every country filled by
 *                             Natural Earth MAPCOLOR7 (adjacent countries never
 *                             share a colour) using a muted, easy-on-the-eye palette
 */

// ── Styling constants ──────────────────────────────────────
import { DESERT_COUNTRIES, isDesertCountry } from '../config.js'

const BOUNDARY_COLOR = [255, 210, 0, 200]
const BOUNDARY_COLOR_DIM = [255, 210, 0, 140]
const BOUNDARY_COLOR_DESERT = [255, 20, 147, 200]
const BOUNDARY_COLOR_DESERT_DIM = [255, 20, 147, 140]
const BOUNDARY_WIDTH = 1.5

// ── Population choropleth presets (ported from V1) ──────────
const VIEW_CONFIG = {
  population: {
    property: 'population',
    breaks: [100000, 1000000, 10000000, 50000000],
    colors: [
      [239, 243, 255, 180], // <100K
      [189, 215, 231, 180], // 100K–1M
      [107, 174, 214, 180], // 1M–10M
      [49, 130, 189, 180], // 10M–50M
      [8, 81, 156, 180], // 50M+
    ],
    label: 'Population',
  },
  density: {
    property: 'pop_density_km2',
    breaks: [10, 100, 500, 2000],
    colors: [
      [254, 229, 217, 180], // <10
      [252, 174, 145, 180], // 10–100
      [251, 106, 74, 180], // 100–500
      [222, 45, 38, 180], // 500–2000
      [165, 15, 21, 180], // 2000+
    ],
    label: 'Population Density (per km²)',
  },
  share: {
    property: 'pop_pct_national',
    breaks: [2, 5, 15, 30],
    colors: [
      [242, 240, 247, 180], // <2%
      [203, 201, 226, 180], // 2–5%
      [158, 154, 200, 180], // 5–15%
      [117, 107, 177, 180], // 15–30%
      [84, 39, 143, 180], // 30%+
    ],
    label: 'Share of National Population (%)',
  },
}

// ── Political map palette ───────────────────────────────────
// Keyed by Natural Earth MAPCOLOR7 (values 1–7). MAPCOLOR7 is precomputed so
// no two neighbouring countries share a value — a four-colour-theorem style
// political map. Muted mid-tones read well over the satellite basemap at ~0.7
// opacity while staying easy on the eye and clearly distinguishable.
const POLITICAL_PALETTE = {
  1: '#d98e7f', // soft coral
  2: '#e3c283', // warm sand
  3: '#a6c39a', // sage
  4: '#7fb0b5', // dusty teal
  5: '#93a6d6', // periwinkle
  6: '#c7a3c9', // mauve
  7: '#d8a87c', // terracotta
}
const POLITICAL_FALLBACK = '#9aa0a6' // neutral grey for any feature missing MAPCOLOR7

/**
 * Convert [r, g, b, a] (0-255) to CSS rgba string.
 */
function rgbaToCSS([r, g, b, a = 255]) {
  return `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`
}

/**
 * Convert [r, g, b] to hex color string (#RRGGBB).
 */
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
}

/**
 * Get the color for a feature based on step breaks.
 */
function getStepColor(value, breaks, colors) {
  if (value == null || isNaN(value)) return [128, 128, 128, 80]
  for (let i = 0; i < breaks.length; i++) {
    if (value < breaks[i]) return colors[i]
  }
  return colors[colors.length - 1]
}

/**
 * Prepare boundary style for MapLibre native rendering.
 *
 * Returns { geojson, fill, line } where:
 *   - geojson: enriched FeatureCollection with _fill property on each feature
 *   - fill: MapLibre fill paint object (or false to skip)
 *   - line: MapLibre line paint object (or false to skip)
 *
 * @param {object} geojson - GeoJSON FeatureCollection from R2
 * @param {string} mode - 'outline', 'highlight', 'choropleth-population', etc.
 * @param {object} [options] - Extra options (highlightNames, etc.)
 * @returns {{ geojson: object, fill: object|false, line: object|false }}
 */
export function prepareBoundaryStyle(geojson, mode = 'outline', options = {}) {
  if (!geojson?.features?.length) {
    return { geojson, fill: false, line: false }
  }

  const desert = isDesertCountry(geojson)
  const lineColor = desert ? BOUNDARY_COLOR_DESERT : BOUNDARY_COLOR
  const lineColorDim = desert ? BOUNDARY_COLOR_DESERT_DIM : BOUNDARY_COLOR_DIM
  const highlightFill = desert ? '#ff1493' : '#00c8ff'

  if (mode === 'outline') {
    return {
      geojson,
      // Near-zero opacity fill: visually invisible against the basemap, but
      // queryRenderedFeatures hits it so MapLibre's click handler fires popups.
      fill: {
        'fill-color': '#000',
        'fill-opacity': 0.001,
      },
      line: {
        'line-color': rgbaToCSS(lineColor),
        'line-width': BOUNDARY_WIDTH,
      },
    }
  }

  if (mode === 'highlight') {
    const highlightSet = options.highlightNames
      ? new Set(options.highlightNames.map((n) => n.toLowerCase()))
      : null

    const enriched = {
      type: 'FeatureCollection',
      features: geojson.features.map((f) => {
        const name = (f.properties?.shapeName || '').toLowerCase()
        const isHighlighted = !highlightSet || highlightSet.has(name)
        return {
          ...f,
          properties: {
            ...f.properties,
            _fill: isHighlighted ? highlightFill : '#000000',
            _opacity: isHighlighted ? 0.31 : 0,
          },
        }
      }),
    }

    return {
      geojson: enriched,
      fill: {
        'fill-color': ['coalesce', ['get', '_fill'], '#000000'],
        'fill-opacity': ['coalesce', ['get', '_opacity'], 0],
      },
      line: {
        'line-color': rgbaToCSS(lineColor),
        'line-width': 2,
      },
    }
  }

  if (mode === 'political') {
    const enriched = {
      type: 'FeatureCollection',
      features: geojson.features.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          _fill: POLITICAL_PALETTE[f.properties?.MAPCOLOR7] || POLITICAL_FALLBACK,
        },
      })),
    }

    return {
      geojson: enriched,
      fill: {
        'fill-color': ['coalesce', ['get', '_fill'], POLITICAL_FALLBACK],
        'fill-opacity': 0.7,
      },
      line: {
        'line-color': 'rgba(20,20,30,0.55)',
        'line-width': 0.6,
      },
    }
  }

  if (mode.startsWith('choropleth-')) {
    const metric = mode.replace('choropleth-', '')
    const config = VIEW_CONFIG[metric]
    if (!config) {
      console.warn(`[BoundaryLayer] Unknown choropleth metric: ${metric}`)
      return { geojson, fill: false, line: false }
    }

    const enriched = {
      type: 'FeatureCollection',
      features: geojson.features.map((f) => {
        const val = f.properties?.[config.property]
        const color = getStepColor(Number(val), config.breaks, config.colors)
        return {
          ...f,
          properties: {
            ...f.properties,
            _fill: rgbToHex(color),
          },
        }
      }),
    }

    return {
      geojson: enriched,
      fill: {
        'fill-color': ['coalesce', ['get', '_fill'], '#808080'],
        'fill-opacity': 0.7,
      },
      line: {
        'line-color': rgbaToCSS(lineColorDim),
        'line-width': BOUNDARY_WIDTH,
      },
    }
  }

  // Unknown mode — outline fallback
  return {
    geojson,
    fill: false,
    line: {
      'line-color': rgbaToCSS(lineColor),
      'line-width': BOUNDARY_WIDTH,
    },
  }
}

/**
 * Get the VIEW_CONFIG for building legends.
 */
export function getViewConfig() {
  return VIEW_CONFIG
}

/**
 * Build legend stops from a choropleth metric for the Legend UI.
 */
export function buildLegendConfig(metric) {
  const config = VIEW_CONFIG[metric]
  if (!config) return null

  const labels = {
    population: ['< 100K', '100K – 1M', '1M – 10M', '10M – 50M', '50M+'],
    density: ['< 10', '10 – 100', '100 – 500', '500 – 2K', '2K+'],
    share: ['< 2%', '2 – 5%', '5 – 15%', '15 – 30%', '30%+'],
  }

  const stops = config.colors.map((color, i) => ({
    color: [color[0], color[1], color[2], 255],
    label: labels[metric]?.[i] || '',
  }))

  // Add "No data" entry for features with missing/null population values
  stops.push({
    color: [128, 128, 128, 255],
    label: 'No data',
  })

  return {
    visible: true,
    title: config.label,
    type: 'classified',
    stops,
  }
}

/**
 * Compute bounding box from GeoJSON features.
 * @returns {number[]} [minLng, minLat, maxLng, maxLat]
 */
export function computeBbox(geojson) {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  function walk(coords) {
    if (typeof coords[0] === 'number') {
      if (coords[0] < minLng) minLng = coords[0]
      if (coords[1] < minLat) minLat = coords[1]
      if (coords[0] > maxLng) maxLng = coords[0]
      if (coords[1] > maxLat) maxLat = coords[1]
    } else {
      coords.forEach(walk)
    }
  }
  for (const f of geojson.features || []) {
    if (f.geometry?.coordinates) walk(f.geometry.coordinates)
  }
  return [minLng, minLat, maxLng, maxLat]
}
